import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useApi } from '../../app/providers';
import type { AuthUser } from '../../lib/api';

export type Session = { status: 'loading' } | { status: 'signed-out' } | { status: 'signed-in'; user: AuthUser };

const SessionContext = createContext<Session>({ status: 'loading' });

export const useSession = (): Session => useContext(SessionContext);

/** Tracks the signed-in user; signing out drops every cached query so no data outlives the session. */
export function SessionProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session>({ status: 'loading' });

  useEffect(() => {
    let alive = true;
    const apply = (user: AuthUser | null) => {
      if (alive) setSession(user ? { status: 'signed-in', user } : { status: 'signed-out' });
    };
    api.auth.getUser().then(apply, () => apply(null));
    const off = api.auth.onChange((user) => {
      if (!user) queryClient.clear();
      apply(user);
    });
    return () => {
      alive = false;
      off();
    };
  }, [api, queryClient]);

  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}
